import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant site ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Send active site ID for multi-tenant scoping
  const activeSiteId = localStorage.getItem('activeSiteId');
  if (activeSiteId) {
    config.headers['x-site-id'] = activeSiteId;
  }
  return config;
});

// Add response interceptor to handle module access errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response.data?.module) {
      // This is a module access error
      const moduleError = error.response.data;
      
      // Store the error for UI components to handle
      localStorage.setItem('moduleAccessError', JSON.stringify(moduleError));
      
      // Redirect to the module access error page
      window.location.href = '/module-access-error';
      
      // Prevent the error from propagating
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Types
export interface ModuleSettings {
  [key: string]: any;
}

export interface Module {
  name: string;
  displayName: string;
  description: string;
  icon: string;
  category: string;
  requiredRole: 'admin' | 'super_admin';
  pricing: 'standard' | 'premium';
  enabled?: boolean;
  configured?: boolean;
  configuredAt?: string;
  configuredBy?: string;
  settings?: ModuleSettings;
  canEnable?: boolean;
}

export interface ModuleStatusResponse {
  siteId: string;
  modules: Module[];
  userRole: string;
}

export interface AvailableModulesResponse {
  modules: Module[];
  userRole: string;
}

export interface EnableModuleResponse {
  message: string;
  module: Module;
}

// API Functions
export const modulesAPI = {
  // Get all available modules (system-wide)
  getAvailableModules: () => {
    return api.get<AvailableModulesResponse>('/modules/available');
  },

  // Get module status for a specific site
  getModuleStatus: (siteId: string) => {
    return api.get<ModuleStatusResponse>(`/modules/sites/${siteId}/status`);
  },

  // Enable a module for a site
  enableModule: (siteId: string, moduleName: string, settings?: ModuleSettings) => {
    return api.post<EnableModuleResponse>(`/modules/sites/${siteId}/${moduleName}/enable`, {
      settings
    });
  },

  // Disable a module for a site
  disableModule: (siteId: string, moduleName: string) => {
    return api.post<EnableModuleResponse>(`/modules/sites/${siteId}/${moduleName}/disable`);
  },

  // Update module settings for a site
  updateModuleSettings: (siteId: string, moduleName: string, settings: ModuleSettings) => {
    return api.put<EnableModuleResponse>(`/modules/sites/${siteId}/${moduleName}/settings`, {
      settings
    });
  }
};

export default modulesAPI;
